import { Play, Pause, SkipForward, SkipBack, Square, Video } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useLanguage } from "@/hooks/useLanguage";
import { showcase, useShowcaseMode } from "@/lib/showcase/showcaseMode";
import { SHOWCASE_SCRIPT } from "@/lib/showcase/showcaseScript";

/**
 * Floating, sponsor-clean controls for the showcase sequence.
 * Doubles as a Creator/Streamer mode toggle.
 */
export function ShowcaseControls() {
  const { t } = useLanguage();
  const s = useShowcaseMode();
  const total = SHOWCASE_SCRIPT.length;

  return (
    <div
      role="toolbar"
      aria-label={t("sc_controls_label")}
      className="pointer-events-auto inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/85 px-1.5 py-1 backdrop-blur shadow-lg"
    >
      {!s.active ? (
        <button
          onClick={() => showcase.start()}
          className="flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground active:scale-95"
        >
          <Play className="h-3.5 w-3.5" /> {t("sc_start")}
        </button>
      ) : (
        <>
          <button
            onClick={() => showcase.prev(total)}
            className="rounded-full p-1.5 hover:bg-accent"
            aria-label={t("sc_prev")}
          >
            <SkipBack className="h-3.5 w-3.5" />
          </button>
          {s.playing ? (
            <button
              onClick={() => showcase.pause()}
              className="rounded-full bg-primary p-1.5 text-primary-foreground active:scale-95"
              aria-label={t("sc_pause")}
            >
              <Pause className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              onClick={() => showcase.resume()}
              className="rounded-full bg-primary p-1.5 text-primary-foreground active:scale-95"
              aria-label={t("sc_play")}
            >
              <Play className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            onClick={() => showcase.next(total)}
            className="rounded-full p-1.5 hover:bg-accent"
            aria-label={t("sc_next")}
          >
            <SkipForward className="h-3.5 w-3.5" />
          </button>
          <span className="px-1.5 text-[10px] font-bold tabular-nums text-muted-foreground">
            {s.beatIndex + 1}/{total}
          </span>
          <button
            onClick={() => showcase.stop()}
            className="rounded-full p-1.5 hover:bg-accent"
            aria-label={t("sc_stop")}
          >
            <Square className="h-3.5 w-3.5" />
          </button>
        </>
      )}
      <span aria-hidden className="mx-1 h-4 w-px bg-border" />
      <Link
        to="/creator-studio"
        className="flex items-center gap-1 rounded-full bg-foreground px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-background transition hover:opacity-90"
        aria-label="Abrir Creator Studio"
      >
        <Video className="h-3 w-3" /> {t("sc_creator")}
      </Link>
    </div>
  );
}
